<template>
  <div class="w-full p-6 bg-white rounded-lg shadow">
    <h4 class="text-base font-semibold">{{ $t('widgets.sections.snowreport.title') }}</h4>
    <p class="text-sm text-grey">{{ $t('widgets.sections.snowreport.description') }}</p>

    <SnowReportFieldsSelector v-model="internalFields" />

    <div class="mt-4">
      <button class="sk-btn sk-btn-primary btn-border rounded-lg" @click="checkSnowReportData">
        {{ $t('widgets.actions.check_data') }}
      </button>
    </div>

    <SKModal
      ref="snowReportModal"
      :title="modalTitle"
      closeButtonKey="apps.modals.cancel"
      actionButtonsPosition="center"
    >
      <template #body>
        <div class="min-w-[320px]">
          <div v-if="modalWarnings.length" class="mb-4">
            <div
              v-for="(warning, index) in modalWarnings"
              :key="index"
              class="text-danger text-sm"
            >
              {{ warning }}
            </div>
          </div>

          <div class="mb-4">
            <h5 class="text-sm font-semibold mb-2">
              {{ $t('widgets.modal.fields.title') }}
            </h5>
            <div v-if="modalRows.length === 0" class="text-sm text-grey">
              {{ $t('widgets.modal.no_data') }}
            </div>
            <div
              v-for="(row, rowIndex) in modalRows"
              :key="rowIndex"
              class="flex items-center justify-between py-1"
            >
              <span class="text-sm">{{ row.label }}</span>
              <span class="text-sm font-medium">{{ row.value }}</span>
            </div>
          </div>
        </div>
      </template>
    </SKModal>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import SnowReportService from '@/modules/Resorts/Services/SnowReportService'
import SnowReportSettingsService from '@/modules/Apps/Services/SnowReportSettingsService'
import { unifyPaginatedResponse } from '@/modules/Shared/Helpers/apiResponseHelper'
import { getFormatInSelectSnowTypes } from '@/modules/Resorts/Helpers/SnowReportHelper'
import SKModal from '@/modules/Shared/Components/modal/SKModal.vue'
import SnowReportFieldsSelector from '@/modules/Apps/Pages/widgets/components/SnowReportFieldsSelector.vue'
import loader from '@/modules/Shared/Mixins/loader'

export default {
  name: 'SnowReportSection',
  components: {
    SKModal,
    SnowReportFieldsSelector
  },
  mixins: [loader],
  props: {
    value: {
      type: Array,
      default: () => []
    },
    selectedResortUuid: {
      type: String,
      required: true
    },
    dataOrigin: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      snowReportUpdatedAt: null,
      modalTitle: '',
      modalWarnings: [],
      modalRows: [],
      internalFields: [],
    }
  },
  watch: {
    value: function (newValue) {
      this.internalFields = newValue
    },
    internalFields: function (newValue) {
      this.$emit('input', newValue)
    }
  },
  methods: {
    async checkSnowReportData() {
      this.modalWarnings = []
      this.modalRows = []
      const success = await this.loadAndIgnoreError(async () => {
        const report = await this.fetchSnowReportByOrigin()
        const warnings = []
        const updatedAt = await this.getSnowReportUpdatedAt()
        if (!report) {
          warnings.push(this.$t('widgets.modal.warning.no_data'))
        }
        if (this.isOutdated(updatedAt, 14)) {
          warnings.push(this.$t('widgets.modal.warning.outdated_14'))
        }

        const rows = report ? this.buildSnowReportRows(report) : []
        this.openModal(this.$t('widgets.modal.snowreport.title'), warnings, rows)
      })
      if (!success) {
        this.notifyError(this.$t('shops.error.0'), this.$t('widgets.notifications.fetch_snowreport_error'))
      }
    },
    async fetchSnowReportByOrigin() {
      if (this.dataOrigin === 'general') {
        const response = await SnowReportService.getSnowReport(this.selectedResortUuid)
        return this.normalizeSnowReportResponse(response)
      }
      const zones = await unifyPaginatedResponse(
        SnowReportSettingsService.getSnowReportZones,
        this.selectedResortUuid
      )
      const zone = Array.isArray(zones) ? zones.find(zone => zone.id === parseInt(this.dataOrigin)) : null
      return zone || null
    },
    normalizeSnowReportResponse(response) {
      if (!response) {
        return null
      }
      if (Array.isArray(response.data)) {
        return response.data[0] || null
      }
      return response
    },
    async getSnowReportUpdatedAt() {
      if (this.snowReportUpdatedAt) {
        return this.snowReportUpdatedAt
      }
      const response = await SnowReportService.getSnowReport(this.selectedResortUuid)
      const report = this.normalizeSnowReportResponse(response)
      this.snowReportUpdatedAt = this.getUpdatedTimestamp(report)
      return this.snowReportUpdatedAt
    },
    getUpdatedTimestamp(report) {
      if (!report) {
        return null
      }
      if (report.updated_timestamp) {
        return report.updated_timestamp
      }
      if (typeof report.updated === 'number') {
        return report.updated
      }
      if (report.updated) {
        const parsed = DateTime.fromFormat(report.updated, 'yyyy-LL-dd HH:mm:ss')
        return parsed.isValid ? parsed.toSeconds() : null
      }
      if (report.modified) {
        return report.modified
      }
      return null
    },
    isOutdated(timestampSeconds, maxDays) {
      if (!timestampSeconds) {
        return false
      }
      const timestamp = DateTime.fromSeconds(timestampSeconds)
      return DateTime.now().diff(timestamp, 'days').days > maxDays
    },
    buildSnowReportRows(report) {
      return this.internalFields.reduce((rows, fieldKey) => {
        if (fieldKey === 'thickness') {
          const min = report && report.min_snow
          const max = report && report.max_snow
          if (min == null || max == null) {
            rows.push({
              label: this.fieldLabel(fieldKey),
              value: this.$t('widgets.modal.values.missing_minmax')
            })
          } else {
            rows.push({ label: this.fieldLabel(fieldKey), value: `${min} - ${max}` })
          }
          return rows
        }

        const value = report ? report[fieldKey] : null
        if (value == null || value === '') {
          rows.push({
            label: this.fieldLabel(fieldKey),
            value: this.$t('widgets.modal.values.missing')
          })
          return rows
        }

        if (fieldKey === 'snow_type') {
          const formatted = getFormatInSelectSnowTypes(value)
          rows.push({
            label: this.fieldLabel(fieldKey),
            value: (formatted && formatted.label) || value
          })
          return rows
        }

        if (['last_snowfall', 'season_opening_date', 'season_closing_date'].includes(fieldKey)) {
          const dateValue = this.formatDateValue(value)
          rows.push({ label: this.fieldLabel(fieldKey), value: dateValue })
          return rows
        }

        if (fieldKey === 'open') {
          rows.push({
            label: this.fieldLabel(fieldKey),
            value: value
              ? this.$t('widgets.modal.values.open')
              : this.$t('widgets.modal.values.closed')
          })
          return rows
        }

        rows.push({ label: this.fieldLabel(fieldKey), value: String(value) })
        return rows
      }, [])
    },
    fieldLabel(fieldKey) {
      const keyToTranslation = {
        open: 'snowreport.label.resortstatus',
        avalanche_risk: 'snowreport.label.avalancherisk',
        open_slopes: 'snowreport.label.openslopes',
        open_lifts: 'snowreport.label.openlifts',
        skiable_km: 'snowreport.label.distance',
        temp_min: 'snowreport.label.mintemperature',
        temp_max: 'snowreport.label.maxtemperature',
        wind_speed: 'snowreport.label.windspeed',
        min_snow: 'snowreport.label.minsnowdepth',
        max_snow: 'snowreport.label.maxsnowdepth',
        mid_snow: 'snowreport.label.midmountainsnowdepth',
        snowfall_24: 'snowreport.label.snowfall24h',
        snowfall_48: 'snowreport.label.snowfall48h',
        snowfall_72: 'snowreport.label.snowfall72h',
        snowfall_season: 'snowreport.label.snowfallseason',
        snowfall_overnight: 'snowreport.label.snowfallovernight',
        snow_type: 'snowreport.label.snowfallcondition',
        base_snow: 'snowreport.label.basesnow',
        new_snow: 'snowreport.label.newsnow',
        last_snowfall: 'snowreport.label.lastsnowfall',
        season_opening_date: 'snowreport.label.seasonopeningdate',
        season_closing_date: 'snowreport.label.seasonclosingdate',
        conditions_summary: 'snowreport.label.conditionssummary',
        anouncement: 'snowreport.label.anouncement',
        resortsummary: 'snowreport.label.resortsummary',
        weather_report: 'snowreport.label.weatherreport',
        opening_hours: 'snowreport.label.openinghours',
        thickness: 'widgets.fields.thickness'
      }
      const translationKey = keyToTranslation[fieldKey]
      return translationKey ? this.$t(translationKey) : fieldKey
    },
    formatDateValue(value) {
      if (!value) {
        return this.$t('widgets.modal.values.missing')
      }
      if (typeof value === 'number') {
        return DateTime.fromSeconds(value).toFormat('dd-LL-yyyy')
      }
      if (DateTime.fromISO(value).isValid) {
        return DateTime.fromISO(value).toFormat('dd-LL-yyyy')
      }
      const fromFormat = DateTime.fromFormat(value, 'yyyy-LL-dd')
      return fromFormat.isValid ? fromFormat.toFormat('dd-LL-yyyy') : value
    },
    openModal(title, warnings, rows) {
      this.modalTitle = title
      this.modalWarnings = warnings
      this.modalRows = rows
      this.$refs.snowReportModal.open()
    }
  }
}
</script>

